import { Alert, AlertTitle, Box, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useEffect, type JSX } from "react";

import { useToast } from "@crm-src/hooks/use-toast";

/**
 * Toast container — the surface that actually RENDERS the app's one toast
 * store. Ported from xop-edge-staff's `components/ui/toaster.tsx` so the
 * same CRM code gives a reseller the same feedback XOP staff get.
 *
 * Until this file existed, nothing read the store: the CRM's ~50 call sites
 * dispatched `toast()` into it and the messages were never drawn. Silent
 * successes were merely confusing; silent FAILURES were the damage — e.g.
 * crm-api's 409 on a duplicate company name is deliberately worded to tell a
 * reseller enough to stop retrying without revealing which other reseller
 * holds that company, and nobody ever read it.
 *
 * **Why MUI and not shadcn's Radix `Toast`.** `hooks/use-toast.ts` is a
 * shadcn port, and `components/ui/toast.tsx` still holds the matching Radix
 * primitives — but those primitives are styled entirely with Tailwind
 * classes, and this repo has no Tailwind (no config, no `tailwindcss`
 * dependency; `tailwind-merge` alone only merges class strings, it generates
 * no CSS). A shadcn Toaster here would render unstyled. The store was itself
 * ported from the MUI-flavoured edge-staff version — `severity`,
 * `TOAST_LIMIT = 3`, the short remove delay — so MUI `Alert`s are what it was
 * written for. `toast.tsx` survives only as the source of the `ToastProps` /
 * `ToastActionElement` types the store imports.
 *
 * Shape: a fixed bottom-left COLUMN that stacks up to `TOAST_LIMIT` at once,
 * each severity-coloured and auto-dismissing on its own timer. (A single
 * `Snackbar` would show one toast at one spot, so a burst of events would
 * overlap invisibly.) The imperative `toast()` / `useToast()` API is
 * untouched, so every existing call site works as written.
 */
const DEFAULT_DURATION_MS = 6000;

type StoreToast = ReturnType<typeof useToast>["toasts"][number];

function ToastItem({ toast }: { toast: StoreToast }): JSX.Element | null {
  const open = toast.open ?? true;

  // Each toast auto-dismisses on its OWN timer, so a later arrival does not
  // extend or cut short an earlier one. The store object's identity is stable
  // until that toast is updated or dismissed, so a sibling arriving does not
  // reset this timer.
  useEffect(() => {
    if (!open) return;
    const id = setTimeout(
      () => toast.onOpenChange?.(false),
      toast.duration ?? DEFAULT_DURATION_MS,
    );
    return () => clearTimeout(id);
  }, [toast, open]);

  if (!open) return null;

  // Callers that care set `severity` directly; the CRM's ported call sites
  // use the shadcn `variant` ("destructive" → red error).
  const severity =
    toast.severity ?? (toast.variant === "destructive" ? "error" : "info");
  const close = (): void => toast.onOpenChange?.(false);

  return (
    <Alert
      variant="filled"
      severity={severity}
      sx={{
        width: "100%",
        alignItems: "flex-start",
        boxShadow: 6,
        "@keyframes xopToastIn": {
          from: { opacity: 0, transform: "translateY(8px) scale(0.98)" },
          to: { opacity: 1, transform: "none" },
        },
        animation: "xopToastIn 180ms ease-out",
        "@media (prefers-reduced-motion: reduce)": { animation: "none" },
      }}
      action={
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          {toast.action}
          <IconButton
            size="small"
            color="inherit"
            aria-label="Close"
            data-testid="toast-close"
            onClick={close}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      }
    >
      {toast.title && (
        <AlertTitle sx={{ mb: toast.description ? 0.5 : 0 }}>
          {toast.title}
        </AlertTitle>
      )}
      {toast.description}
    </Alert>
  );
}

export function Toaster(): JSX.Element {
  const { toasts } = useToast();

  return (
    <Box
      data-testid="toaster"
      sx={{
        position: "fixed",
        bottom: 16,
        // Bottom-LEFT, matching the command center exactly. The reason that
        // repo chose left (its Tauri shell puts OS notification popups
        // bottom-right) does not apply to this web-only portal, but a
        // reseller who has also seen the command center should find feedback
        // in the same corner, so the position is kept rather than "fixed".
        left: 16,
        zIndex: (theme) => theme.zIndex.snackbar,
        display: "flex",
        flexDirection: "column",
        gap: 1,
        width: "min(420px, calc(100vw - 32px))",
        // The container spans a column but only the toasts themselves should
        // catch clicks — everything in the gaps must fall through to the page
        // (and to the rail underneath).
        pointerEvents: "none",
        "& > *": { pointerEvents: "auto" },
      }}
    >
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} />
      ))}
    </Box>
  );
}
